import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';
import { orderBy } from 'lodash';

import { KML_FEATURE_TYPE } from '../../../featureTypes';
import { WHITE } from '../../../rgbColors';
import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { MAP_EVENTS } from '../../../mapEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { convertProjection4329 } from '../../../utils';
import { getPathKey, getSelectedFeature } from '../helpers/features';
import selectionManager from '../../../services/SelectionManager';
import { hideAtZoom, HIDE_AT_ZOOM_ZONE } from './zoomThresholds';

interface PathLayerProps {
  zoneMission: boolean;
}

interface PathLayerState {
  features: JSX.Element[];
}

const Z_INDEX_PATH = 1;
const Z_INDEX_WAYPOINT = 2;

/**
 * PathLayer — focused layer that renders the waypoint path line and its
 * snap points. Step 3 of the KmlLayer split. See
 * docs/superpowers/plans/2026-04-08-kml-layer-split.md.
 *
 * Subscribes to:
 *   - MISSION_EVENTS.CREATED_OR_DELETED (mission swap)
 *   - MAP_EVENTS.CORES_NEED_UPDATE (path recalculations: reverse_path,
 *     smart_path, update_path, reorder, handleSkip, undo, etc.)
 *   - MAP_EVENTS.PATH_UPDATED (PointsUpdater non-renumber branch and
 *     LassoService move — centroid.displace moves waypoints without
 *     a full core rebuild)
 *   - MAP_EVENTS.SELECTION_CHANGED (width 4 vs 2 when path is selected)
 *
 * NOT subscribed to MISSION_EVENTS.UPDATED — too noisy; path changes
 * are fully covered by CORES_NEED_UPDATE + PATH_UPDATED.
 */
export default class PathLayer extends PureComponent<PathLayerProps, PathLayerState> {
  constructor(props: PathLayerProps) {
    super(props);
    this.state = { features: [] };
    this.rebuildAll = this.rebuildAll.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
  }

  /** Was the path selected on the last render? */
  private prevPathSelected = false;

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.on(MAP_EVENTS.PATH_UPDATED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    this.rebuildAll();
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.PATH_UPDATED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
  }

  /** Only rebuild when the path's own selected state flipped. */
  private onSelectionChanged() {
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const isPathSelected = featureType === KML_FEATURE_TYPE.PATH && !!selectedFeature;

    if (isPathSelected !== this.prevPathSelected) {
      this.rebuildAll();
    }
  }

  rebuildAll() {
    console.count('PathLayer.rebuildAll');
    const mission = getCurrentMission();

    if (!mission) {
      this.setState({ features: [] });

      return;
    }

    const waypoints = mission.getWaypoints();

    if (waypoints.length === 0) {
      this.setState({ features: [] });

      return;
    }

    // Coordinates of the path in waypoint order. convertProjection4329
    // matches what KmlLayer used to do via mission.to_feature for the
    // path feature.
    const coordinates = orderBy(waypoints, ['waypoint_number'], ['asc']).map((wp) =>
      convertProjection4329([wp.lon, wp.lat]),
    );

    // Click-to-select visual (width 4 vs 2) restored via shared
    // SelectionManager. Mirrors the old KmlLayer.genPathFeature logic
    // — key is 'path' for this layer's single LineString.
    const pathProperties = { name: 'path', visibility: true } as const;
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.PATH &&
      !!selectedFeature &&
      getPathKey(pathProperties) === getPathKey(selectedFeature.getProperties() as { name?: string });
    this.prevPathSelected = selected;

    const uniqueKey = `path-${KML_FEATURE_TYPE.PATH}`;
    const features: JSX.Element[] = [
      <feature.LineStringReact
        coordinates={coordinates}
        strokeOptions={{ color: WHITE, width: selected ? 4 : 2 }}
        properties={pathProperties}
        key={`${uniqueKey}-line`}
        zIndex={Z_INDEX_PATH}
        forceRefresh={true}
        hideAtZoom={HIDE_AT_ZOOM_ZONE}
      />,
      ...coordinates.map((coord, i) => (
        <feature.PointReact
          key={`${uniqueKey}-snap-${i}`}
          coordinate={coord}
          circleOptions={{
            radius: 3,
            fillOptions: { color: '#4750DD' },
            strokeOptions: { color: 'black', width: 1 },
          }}
          zIndex={Z_INDEX_WAYPOINT}
          hideAtZoom={hideAtZoom(this.props.zoneMission)}
        />
      )),
    ];

    this.setState({ features });
  }

  render() {
    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_PATH}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
